import { useState } from 'react';
import { Award, MessageSquare, Calendar, ChevronDown, Ship, Anchor, CheckCircle, Clock, XCircle, User, Trophy, Timer } from 'lucide-react';

const SUPPORT_ICONS = { 'VNM': Ship, 'BATEAU': Ship, 'ENGINS TRACTÉS': Anchor, 'ENGINS TRACTES': Anchor };

const CarnetNotesTuteur = ({
  evaluations = [],
  heuresParCategorie = {},
  autonomiesValidees = [],
  title = "Carnet de Notes - Tuteur",
}) => {
  const [expandedSupport, setExpandedSupport] = useState(null);

  // Filtrer les évaluations "Validation du Support" qui ne sont pas des notes mais des validations autonomes
  const filtered = evaluations.filter(e => e.sous_categorie !== 'Validation du Support');

  // Extraire le SUPPORT depuis item (enlever préfixes BRIEFING/AISANCE/ENCADREMENT)
  const extractSupport = (item) => {
    const raw = (item || '').toString().trim();
    if (!raw) return 'Autre';
    return raw.toUpperCase()
      .replace('BRIEFING ', '')
      .replace('AISANCE ', '')
      .replace('ENCADREMENT ', '')
      .trim() || raw;
  };

  // Group par SUPPORT puis catégorie
  const supportsMap = {};
  filtered.forEach(e => {
    const support = extractSupport(e.item);
    const cat = e.categorie || 'Autre';
    if (!supportsMap[support]) supportsMap[support] = {};
    if (!supportsMap[support][cat]) supportsMap[support][cat] = [];
    supportsMap[support][cat].push(e);
  });
  const supports = Object.keys(supportsMap).sort();

  // Heures par support (si calculées) — total ET détail par sous-catégorie
  const heuresParSupport = {};        // { support: total }
  const heuresParSousCat = {};        // { support: { sousCat: heures } }
  if (typeof heuresParCategorie === 'object') {
    Object.values(heuresParCategorie).forEach(data => {
      if (data && typeof data === 'object' && data.sousCat) {
        Object.entries(data.sousCat).forEach(([sc, h]) => {
          const sup = extractSupport(sc);
          heuresParSupport[sup] = (heuresParSupport[sup] || 0) + h;
          if (!heuresParSousCat[sup]) heuresParSousCat[sup] = {};
          heuresParSousCat[sup][sc] = (heuresParSousCat[sup][sc] || 0) + h;
        });
      }
    });
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '';

  const getStatutIcon = (statut) => {
    if (statut === 'competent') return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (statut === 'en_cours') return <Clock className="h-4 w-4 text-orange-500" />;
    if (statut === 'non_competent') return <XCircle className="h-4 w-4 text-red-500" />;
    return <Clock className="h-4 w-4 text-slate-400" />;
  };
  const getStatutLabel = (statut) => {
    if (statut === 'competent') return 'Validé';
    if (statut === 'en_cours') return 'En cours';
    if (statut === 'non_competent') return 'Non acquis';
    return 'Non évalué';
  };
  const getStatutBadgeStyle = (statut) => {
    if (statut === 'competent') return 'bg-green-100 text-green-700 border-green-300';
    if (statut === 'en_cours') return 'bg-orange-100 text-orange-700 border-orange-300';
    if (statut === 'non_competent') return 'bg-red-100 text-red-700 border-red-300';
    return 'bg-slate-100 text-slate-500 border-slate-300';
  };

  const catBadge = (cat) => {
    if (cat === 'Aisance') return 'text-sky-700 bg-sky-50 border-sky-200';
    if (cat === 'Briefing') return 'text-amber-700 bg-amber-50 border-amber-200';
    if (cat === 'Encadrement') return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    if (cat === 'Pédagogie') return 'text-purple-700 bg-purple-50 border-purple-200';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  };

  const stats = {
    total: filtered.length,
    competent: filtered.filter(e => e.statut === 'competent').length,
    en_cours: filtered.filter(e => e.statut === 'en_cours').length,
    non_competent: filtered.filter(e => e.statut === 'non_competent').length,
  };

  return (
    <div className="space-y-4" data-testid="carnet-notes-tuteur">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-display font-semibold text-[#0F172A] flex items-center gap-2">
          <Award className="h-5 w-5 text-orange-500" />
          {title}
        </h2>
      </div>

      {/* Autonomies validées (acceptées tuteur + entreprise + stagiaire) */}
      {autonomiesValidees && autonomiesValidees.length > 0 && (
        <div
          className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-lg p-4"
          data-testid="autonomies-validees-section"
        >
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-5 w-5 text-amber-600" />
            <h3 className="font-display font-semibold text-amber-900">
              Autonomies validées ({autonomiesValidees.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {autonomiesValidees.map((a, i) => (
              <div
                key={`${a.categorie}-${a.sous_categorie}-${i}`}
                className="bg-white border border-amber-300 rounded-lg p-3 shadow-sm"
                data-testid={`autonomie-validee-${a.sous_categorie}`}
              >
                {/* Header : titre + heures */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <CheckCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 leading-tight">
                        {a.sous_categorie}
                      </p>
                      <p className="text-xs text-slate-500">{a.categorie}</p>
                    </div>
                  </div>
                  {a.heures_encadrement > 0 && (
                    <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded">
                      <Timer className="h-3 w-3" />
                      {a.heures_encadrement} h
                    </span>
                  )}
                </div>

                {/* Appréciation tuteur */}
                {a.appreciation_tuteur && (
                  <div className="mt-2 text-xs bg-slate-50 border-l-2 border-green-400 px-2 py-1.5 rounded-r">
                    <p className="font-semibold text-slate-600 mb-0.5 flex items-center gap-1">
                      <User className="h-3 w-3" /> Tuteur
                    </p>
                    <p className="text-slate-700 italic leading-snug">{a.appreciation_tuteur}</p>
                  </div>
                )}

                {/* Appréciation entreprise */}
                {a.appreciation_entreprise && (
                  <div className="mt-1.5 text-xs bg-slate-50 border-l-2 border-blue-400 px-2 py-1.5 rounded-r">
                    <p className="font-semibold text-slate-600 mb-0.5">Entreprise</p>
                    <p className="text-slate-700 italic leading-snug">{a.appreciation_entreprise}</p>
                  </div>
                )}

                {/* Dates */}
                {(a.date_tuteur || a.stagiaire_date) && (
                  <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-400">
                    {a.date_tuteur && (
                      <span>Validée le {formatDate(a.date_tuteur)}</span>
                    )}
                    {a.stagiaire_date && (
                      <span>Acceptée le {formatDate(a.stagiaire_date)}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-xl font-bold text-[#0F172A]">{stats.total}</p>
          <p className="text-xs text-slate-600">Évaluations</p>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
          <p className="text-xl font-bold text-green-600">{stats.competent}</p>
          <p className="text-xs text-green-700">Validées</p>
        </div>
        <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
          <p className="text-xl font-bold text-orange-600">{stats.en_cours}</p>
          <p className="text-xs text-orange-700">En cours</p>
        </div>
        <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-xl font-bold text-red-600">{stats.non_competent}</p>
          <p className="text-xs text-red-700">Non acquis</p>
        </div>
      </div>

      {/* Supports */}
      {supports.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-6 text-center text-slate-500 text-sm">
          Aucune évaluation tuteur pour le moment.
        </div>
      ) : (
        <div className="space-y-3">
          {supports.map(support => {
            const IconSupport = SUPPORT_ICONS[support] || Award;
            const cats = supportsMap[support];
            const allEval = Object.values(cats).flat();
            const nbTotal = allEval.length;
            const nbCompetent = allEval.filter(e => e.statut === 'competent').length;
            const isOpen = expandedSupport === support;

            return (
              <div key={support} className="bg-white border-2 border-orange-200 rounded-lg overflow-hidden shadow-sm" data-testid={`carnet-tuteur-support-${support}`}>
                <button
                  onClick={() => setExpandedSupport(isOpen ? null : support)}
                  className="w-full flex items-center justify-between p-4 hover:bg-orange-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <IconSupport className="h-5 w-5 text-orange-500" />
                    <div className="text-left">
                      <p className="font-display font-semibold text-[#0F172A]">{support}</p>
                      <p className="text-xs text-slate-500">
                        {nbTotal} évaluation{nbTotal > 1 ? 's' : ''} — {nbCompetent} validée{nbCompetent > 1 ? 's' : ''}
                        {heuresParSupport[support] ? ` — ${heuresParSupport[support].toFixed(1)} h d'encadrement` : ''}
                      </p>
                    </div>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-orange-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                  <div className="border-t border-orange-100 divide-y divide-slate-100">
                    {/* Détail heures par sous-catégorie pour ce support */}
                    {heuresParSousCat[support] && Object.keys(heuresParSousCat[support]).length > 0 && (
                      <div className="px-4 py-3 bg-orange-50/50">
                        <p className="text-xs font-semibold uppercase tracking-wide text-orange-700 mb-2 flex items-center gap-1.5">
                          <Timer className="h-3.5 w-3.5" />
                          Heures par sous-catégorie
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(heuresParSousCat[support])
                            .sort((a, b) => b[1] - a[1])
                            .map(([sc, h]) => (
                              <span
                                key={sc}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-orange-200 rounded text-xs"
                                data-testid={`heures-sc-${sc}`}
                              >
                                <span className="text-slate-700">{sc}</span>
                                <span className="font-semibold text-orange-600">{h.toFixed(1)} h</span>
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                    {Object.entries(cats).map(([cat, evals]) => (
                      <div key={cat} className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 text-xs rounded border font-medium ${catBadge(cat)}`}>{cat}</span>
                          <span className="text-xs text-slate-400">{evals.length} évaluation{evals.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className="space-y-2">
                          {evals
                            .slice()
                            .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
                            .map((e, idx) => (
                              <div key={e.evaluation_id || `${cat}-${idx}`} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                                {/* Sous-catégorie complète (ex: "VNM 2 avec tuteur") */}
                                {e.sous_categorie && (
                                  <p className="text-sm font-semibold text-[#0F172A] mb-1.5">
                                    {e.sous_categorie}
                                  </p>
                                )}
                                <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                                  <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <Calendar className="h-3.5 w-3.5" />
                                    {formatDate(e.date)}
                                    {e.formateur_nom && (
                                      <>
                                        <User className="h-3.5 w-3.5 ml-2" />
                                        <span>{e.formateur_nom}</span>
                                      </>
                                    )}
                                  </div>
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border font-medium ${getStatutBadgeStyle(e.statut)}`}>
                                    {getStatutIcon(e.statut)}
                                    {getStatutLabel(e.statut)}
                                  </span>
                                </div>
                                {e.commentaire && (
                                  <div className="flex items-start gap-2 text-sm text-slate-700 bg-white p-2 rounded border border-slate-100">
                                    <MessageSquare className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
                                    <span className="whitespace-pre-wrap">{e.commentaire}</span>
                                  </div>
                                )}
                                {e.appreciation && (
                                  <div className="mt-1.5 flex items-start gap-2 text-sm text-slate-700 bg-amber-50/50 p-2 rounded border border-amber-100">
                                    <Award className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-[10px] uppercase font-semibold text-amber-700 tracking-wide">Appréciation du tuteur</p>
                                      <p className="whitespace-pre-wrap italic">{e.appreciation}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CarnetNotesTuteur;
